import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {number | null}
   */
  #overflowDeactivateTimer = null;

  /**
   * @type {number}
   */
  #minimumTouchTarget = 44;

  /**
   * @type {number | null}
   */
  #lastHoverBridgeHeight = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClickInteraction);
    this.addEventListener('keydown', this.#handleKeyInteraction);
    document.addEventListener('click', this.#handleOutsideClick);
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();
    this.#readMinimumTouchTarget();
    this.#syncHoverBridgeHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClickInteraction);
    this.removeEventListener('keydown', this.#handleKeyInteraction);
    document.removeEventListener('click', this.#handleOutsideClick);
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#clearOverflowDeactivateTimer();
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    this.#readMinimumTouchTarget();
    this.#syncHoverBridgeHeight();
    this.#syncActiveCompactSubmenuPosition();
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Close the open submenu on Escape and restore focus to its disclosure control.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const item = this.#state.activeItem;
    if (!item) return;

    const control = this.#focusTargetAfterClose(item);
    this.#deactivate(item, { force: true });
    control.focus();
  };

  /**
   * Toggle a submenu from its dedicated disclosure button (click / Enter / Space).
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const item = findMenuItem(button);
    if (!item) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      // Close whatever is currently active. In the overflow panel the initially
      // selected item is tracked as `activeOverflowItem` while the More trigger is
      // the `activeItem`, so deactivating `item` directly would no-op; deactivate
      // the active item instead.
      this.#deactivate(this.#state.activeItem ?? item, { force: true });
      // Closing the overflow panel hides the focused disclosure, so return focus
      // to the visible More trigger; a top-level disclosure stays put.
      const focusTarget = this.#focusTargetAfterClose(item);
      if (focusTarget !== button) focusTarget.focus();
    } else {
      this.activate(event);
    }
  };

  /**
   * The control that should receive focus after a submenu closes. Overflow items
   * live in a panel that is hidden on close, so focus returns to the always-visible
   * More trigger instead of a now-inert control inside the panel.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    if (slot === 'overflow' || slot === 'more') {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) return moreButton;
    }
    return this.#expandableFor(item);
  }

  /**
   * Return the element that carries the `aria-expanded` state for a menu item.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #expandableFor(item) {
    const listItem = item.closest('.menu-list__list-item');
    const disclosure = listItem?.querySelector('[ref="disclosure[]"]');
    return disclosure instanceof HTMLElement ? disclosure : item;
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  get isShowAllMenu() {
    return this.querySelector('.menu-list--show-all') instanceof HTMLElement;
  }

  get isClickInteractionMode() {
    return this.dataset.submenuTrigger === 'click';
  }

  get isCompactDropdownMode() {
    return this.dataset.compactDropdowns === 'true';
  }

  #clearOverflowDeactivateTimer = () => {
    if (this.#overflowDeactivateTimer) {
      clearTimeout(this.#overflowDeactivateTimer);
      this.#overflowDeactivateTimer = null;
    }
  };

  #scheduleOverflowDeactivate = () => {
    this.#clearOverflowDeactivateTimer();
    this.#overflowDeactivateTimer = setTimeout(() => {
      this.#deactivate();
    }, 140);
  };

  #readMinimumTouchTarget = () => {
    const minimumTouchTarget = Number.parseFloat(getComputedStyle(this).getPropertyValue('--minimum-touch-target'));
    this.#minimumTouchTarget = Number.isFinite(minimumTouchTarget) && minimumTouchTarget > 0 ? minimumTouchTarget : 44;
  };

  #syncHoverBridgeHeight = () => {
    // An open submenu expands the header, so measuring now would inflate the gap.
    // Keep the value captured while collapsed until everything closes again.
    if (this.#state.activeItem && this.#lastHoverBridgeHeight !== null) return;

    const bridgeHeight = this.#measureHoverBridgeGap();
    if (bridgeHeight === null || bridgeHeight === this.#lastHoverBridgeHeight) return;
    this.#lastHoverBridgeHeight = bridgeHeight;
    this.style.setProperty('--menu-hover-bridge-height', `${bridgeHeight}px`);
  };

  /**
   * Distance from the bottom of a top-level trigger to the submenu plane at the bottom
   * of the header. That gap is all the bridge needs to span; deriving it from the header
   * height overshoots on tall headers (large logos) and leaves a big invisible hover
   * zone hanging below the dropdown.
   * @returns {number | null}
   */
  #measureHoverBridgeGap() {
    const header = this.headerComponent;
    if (!header) return null;

    const trigger = this.querySelector(
      '.menu-list__list-item:not([slot="overflow"]) > :is(.menu-list__trigger, .menu-list__link)'
    );
    if (!(trigger instanceof HTMLElement)) return null;

    const gap = header.getBoundingClientRect().bottom - trigger.getBoundingClientRect().bottom;
    if (!Number.isFinite(gap)) return null;

    return Math.max(0, Math.ceil(gap));
  }

  /**
   * Whether movement is from the active item into a lower wrapped row.
   * @param {HTMLElement | null} nextItem
   * @returns {boolean}
   */
  #isLowerWrappedRow(nextItem) {
    if (!this.isShowAllMenu) return false;
    if (!(nextItem instanceof HTMLElement)) return false;
    if (!(this.#state.activeItem instanceof HTMLElement)) return false;
    if (nextItem === this.#state.activeItem) return false;

    const activeRow = this.#state.activeItem.closest('.menu-list__list-item');
    const nextRow = nextItem.closest('.menu-list__list-item');
    if (!(activeRow instanceof HTMLElement) || !(nextRow instanceof HTMLElement)) return false;
    if (activeRow === nextRow) return false;

    const activeRect = activeRow.getBoundingClientRect();
    const nextRect = nextRow.getBoundingClientRect();
    const verticalDelta = nextRect.top - activeRect.top;
    const rowTolerance = 4;

    // Calculate dynamic downward span from actual header height so stores with
    // larger logos/headers are handled without fixed spacing assumptions.
    const headerHeight = this.headerComponent?.offsetHeight ?? 0;
    const maxDownwardSpan = headerHeight > 0 ? headerHeight : activeRect.height * 2;
    if (verticalDelta <= rowTolerance || verticalDelta > maxDownwardSpan + rowTolerance) return false;

    // Preserve open state only when moving mostly downward, not laterally.
    const overlapWidth = Math.min(activeRect.right, nextRect.right) - Math.max(activeRect.left, nextRect.left);
    const minOverlap = Math.min(activeRect.width, nextRect.width) * 0.35;
    return overlapWidth >= minOverlap;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | KeyboardEvent | Event} event
   */
  activate = (event) => {
    if (
      this.isClickInteractionMode &&
      event.type !== 'click' &&
      event.type !== 'keydown' &&
      event.type !== 'pointerdown'
    ) {
      return;
    }

    this.#syncHoverBridgeHeight();
    this.#clearOverflowDeactivateTimer();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // Derive the slot context from the list item rather than `event.target`.
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const isDefaultSlot = slot === '';
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    // The More trigger auto-opens the first overflow item's submenu, so the panel has to
    // make room for it. `hasSubmenu` records whether real submenu content is being shown
    // before falling back to the bare overflow list, and drives the height calculation below.
    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);
    const isCompactAnchoredSubmenu =
      this.isCompactDropdownMode &&
      isDefaultSlot &&
      submenu instanceof HTMLElement &&
      submenu.dataset.compactDropdown === 'true';

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Wrapped rows: keep current dropdown when crossing downward over non-dropdown items.
    // Same-row lateral movement over non-dropdown items closes immediately.
    if (this.isShowAllMenu && isDefaultSlot && !submenu) {
      if (this.#isLowerWrappedRow(item)) return;
      this.#deactivate(undefined, { force: true });
      return;
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      this.#expandableFor(previouslyActiveItem).ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        previousSubmenu.inert = true;
        previousSubmenu.setAttribute('aria-hidden', 'true');
      }
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      this.#expandableFor(previouslyActiveOverflowItem).ariaExpanded = 'false';
      const previousOverflowSubmenu = findSubmenu(previouslyActiveOverflowItem);
      if (previousOverflowSubmenu) {
        previousOverflowSubmenu.inert = true;
        previousOverflowSubmenu.setAttribute('aria-hidden', 'true');
      }
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    this.#expandableFor(item).ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      this.#expandableFor(overflowItem).ariaExpanded = 'true';
      // The More trigger auto-opens the first overflow item's submenu, so expose it.
      const activeOverflowSubmenu = findSubmenu(overflowItem);
      if (activeOverflowSubmenu) {
        activeOverflowSubmenu.inert = false;
        activeOverflowSubmenu.setAttribute('aria-hidden', 'false');
      }
    }

    if (submenu) {
      this.#positionCompactSubmenu(item, submenu, isDefaultSlot);

      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Expose the open submenu to focus and assistive tech. Collapsed submenus stay
      // inert so their contents cannot be reached (visibility:hidden alone can be
      // overridden by descendants that re-assert visibility).
      submenu.inert = false;
      submenu.setAttribute('aria-hidden', 'false');

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      if (!isCompactAnchoredSubmenu) {
        // Monitor DOM mutations to catch deferred content injection (from section hydration)
        this.#submenuMutationObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
            requestAnimationFrame(() => {
              if (submenu.offsetHeight > 0) {
                this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
                this.#cleanupMutationObserver();
              }
            });
          });
        });
        this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

        // Auto-disconnect after 500ms to prevent memory leaks
        setTimeout(() => {
          this.#cleanupMutationObserver();
        }, 500);
      }
    }

    let finalHeight = isCompactAnchoredSubmenu ? 0 : submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent | Event} event
   */
  deactivate(event) {
    if (this.isClickInteractionMode) {
      return;
    }

    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    // Keep the submenu open while focus moves between the link, its disclosure
    // button, and the submenu contents — i.e. anywhere inside the same list item.
    const isMovingWithinItem =
      event.relatedTarget instanceof Node &&
      event.target instanceof Element &&
      event.target.contains(event.relatedTarget);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      (event.type === 'blur' || event.type === 'pointerleave') &&
      menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));
    const isMovingWithinHeaderMenu =
      event.type === 'pointerleave' && event.relatedTarget instanceof Node && this.contains(event.relatedTarget);
    const nextItem = event.relatedTarget instanceof Element ? findMenuItem(event.relatedTarget) : null;
    const isMovingDownwardFromMoreTrigger = this.#isMovingDownwardFromMoreTrigger(event);

    // Movement rules:
    // - keep open when moving down into the same dropdown path
    // - close when moving sideways to sibling menu items
    if (isMovingWithinItem || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }
    if (!this.isShowAllMenu && isMovingDownwardFromMoreTrigger) {
      this.#scheduleOverflowDeactivate();
      return;
    }
    if (this.isShowAllMenu && isMovingWithinHeaderMenu) {
      if (this.#isLowerWrappedRow(nextItem)) return;
      this.#deactivate(undefined, { force: true });
      return;
    }

    this.#deactivate();
  }

  /**
   * Handle click-to-open desktop interaction mode for menu items with submenus.
   * First click opens submenu, second click on same trigger closes it.
   * @param {MouseEvent} event
   */
  #handleClickInteraction = (event) => {
    if (!this.isClickInteractionMode) return;
    if (!(event.target instanceof Element)) return;

    // Only toggle from top-level triggers; allow submenu links to navigate naturally.
    const trigger = event.target.closest('[ref="menuitem"], .menu-list__list-item[slot="more"] > .menu-list__link');
    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

    // Controls with a declarative click handler are toggled during event capture.
    // Handling them again here would immediately undo that state change.
    if (trigger.hasAttribute('on:click')) return;

    const item = findMenuItem(trigger);
    if (!(item instanceof HTMLElement)) return;

    const slotOwner = item.closest('.menu-list__list-item');
    const isDefaultSlot = ((slotOwner instanceof HTMLElement ? slotOwner.slot : '') ?? '') === '';
    const submenu = findSubmenu(item);

    // Let direct links navigate normally when they don't open a submenu.
    if (!submenu && isDefaultSlot) return;

    const isSameItem = item === this.#state.activeItem;

    // First click opens.
    if (!isSameItem) {
      event.preventDefault();
      this.activate({ target: item, type: 'click' });
      return;
    }

    // Second click closes the currently open submenu.
    event.preventDefault();
    this.#deactivate(undefined, { force: true });
  };

  /**
   * Handle keyboard behavior for click interaction mode.
   * @param {KeyboardEvent} event
   */
  #handleKeyInteraction = (event) => {
    if (!this.isClickInteractionMode) return;

    // Escape is handled by #onKeydown in every mode, which also restores focus.
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element)) return;

    // Only toggle from top-level triggers; allow submenu links to navigate naturally.
    const trigger = event.target.closest('[ref="menuitem"], .menu-list__list-item[slot="more"] > .menu-list__link');
    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

    const item = findMenuItem(trigger);
    if (!(item instanceof HTMLElement)) return;

    const slotOwner = item.closest('.menu-list__list-item');
    const isDefaultSlot = ((slotOwner instanceof HTMLElement ? slotOwner.slot : '') ?? '') === '';
    const submenu = findSubmenu(item);
    if (!submenu && isDefaultSlot) return;

    // Space should always toggle/open instead of scrolling.
    if (event.key === ' ') {
      event.preventDefault();
      if (item === this.#state.activeItem) {
        this.#deactivate(undefined, { force: true });
      } else {
        this.activate({ target: item, type: 'keydown' });
      }
      return;
    }

    // Enter toggles open/close in click mode.
    event.preventDefault();
    if (item === this.#state.activeItem) {
      this.#deactivate(undefined, { force: true });
    } else {
      this.activate({ target: item, type: 'keydown' });
    }
  };

  /**
   * Close open menu when clicking outside header-menu in click interaction mode.
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    if (!this.isClickInteractionMode) return;
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#deactivate(undefined, { force: true });
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered,
    // unless the close was explicitly requested (Escape or the disclosure toggle),
    // which must close regardless of pointer position.
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.#clearOverflowDeactivateTimer();

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    this.#expandableFor(item).ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      this.#expandableFor(activeOverflowItem).ariaExpanded = 'false';
      const activeOverflowSubmenu = findSubmenu(activeOverflowItem);
      if (activeOverflowSubmenu) {
        activeOverflowSubmenu.inert = true;
        activeOverflowSubmenu.setAttribute('aria-hidden', 'true');
      }
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
      submenu.setAttribute('aria-hidden', 'true');
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  #syncActiveCompactSubmenuPosition() {
    if (!(this.#state.activeItem instanceof HTMLElement)) return;
    const submenu = findSubmenu(this.#state.activeItem);
    if (!(submenu instanceof HTMLElement)) return;

    const slotOwner = this.#state.activeItem.closest('.menu-list__list-item');
    const isDefaultSlot = ((slotOwner instanceof HTMLElement ? slotOwner.slot : '') ?? '') === '';
    this.#positionCompactSubmenu(this.#state.activeItem, submenu, isDefaultSlot);
  }

  /**
   * Keep compact dropdowns aligned to their trigger while leaving room for nested slideouts.
   * @param {HTMLElement} item
   * @param {HTMLElement} submenu
   * @param {boolean} isDefaultSlot
   */
  #positionCompactSubmenu(item, submenu, isDefaultSlot) {
    if (!this.isCompactDropdownMode || !isDefaultSlot || submenu.dataset.compactDropdown !== 'true') {
      submenu.style.removeProperty('--compact-submenu-inline-start');
      submenu.style.removeProperty('--compact-submenu-block-start');
      return;
    }

    const slotOwner = item.closest('.menu-list__list-item');
    if (!(slotOwner instanceof HTMLElement)) return;

    const trigger = item.matches('[ref="menuitem"]') ? item : item.querySelector('[ref="menuitem"]');
    const triggerAnchor =
      trigger instanceof HTMLElement
        ? trigger.querySelector('.menu-list__link-title, .menu-list__link-title + .menu-list__caret, .menu-list__caret')
        : null;
    const itemRect = slotOwner.getBoundingClientRect();
    const triggerRect = trigger instanceof HTMLElement ? trigger.getBoundingClientRect() : itemRect;
    const anchorRect = triggerAnchor instanceof HTMLElement ? triggerAnchor.getBoundingClientRect() : triggerRect;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const inner = submenu.querySelector('.menu-list__submenu-inner');
    const nestedPanel = submenu.querySelector('.compact-menu__panel-list');
    const primaryWidth = inner instanceof HTMLElement && inner.offsetWidth > 0 ? inner.offsetWidth : 240;
    const slideoutWidth = nestedPanel instanceof HTMLElement && nestedPanel.offsetWidth > 0 ? nestedPanel.offsetWidth : primaryWidth;
    const totalWidth = primaryWidth + (submenu.dataset.compactHasNested === 'true' ? slideoutWidth : 0);
    const gutter = 8;
    const minShift = gutter - itemRect.left;
    const maxShift = viewportWidth - gutter - totalWidth - itemRect.left;
    const clampedLeft = Math.max(minShift, Math.min(0, maxShift));
    const verticalGap = 6;
    const anchoredTop = Math.max(0, anchorRect.bottom - itemRect.top + verticalGap);

    submenu.style.setProperty('--compact-submenu-inline-start', `${clampedLeft}px`);
    submenu.style.setProperty('--compact-submenu-block-start', `${anchoredTop}px`);
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * In non-wrapped mode, allow a downward mouse move from "More" toward overflow
   * before closing, using actual header height as the allowable travel span.
   * @param {PointerEvent | FocusEvent} event
   * @returns {boolean}
   */
  #isMovingDownwardFromMoreTrigger(event) {
    if (event.type !== 'pointerleave') return false;
    if (!('clientX' in event) || !('clientY' in event)) return false;
    if (!(this.#state.activeItem instanceof HTMLElement)) return false;

    const activeRow = this.#state.activeItem.closest('.menu-list__list-item');
    if (!(activeRow instanceof HTMLElement)) return false;
    // The More trigger and the overflow items are both part of the overflow panel.
    // `activate` tracks the More button as `activeItem` and the first overflow item as
    // `activeOverflowItem`, so either slot can be active while the panel is open.
    if (activeRow.slot !== 'overflow' && activeRow.slot !== 'more') return false;

    const moreTrigger = this.querySelector('.menu-list__list-item[slot="more"] > .menu-list__link');
    if (!(moreTrigger instanceof HTMLElement)) return false;

    const triggerRect = moreTrigger.getBoundingClientRect();
    const headerHeight = this.headerComponent?.offsetHeight ?? triggerRect.height * 2;
    const maxY = triggerRect.bottom + headerHeight;
    const horizontalTolerance = 8;
    const withinX =
      event.clientX >= triggerRect.left - horizontalTolerance && event.clientX <= triggerRect.right + horizontalTolerance;
    const downward = event.clientY >= triggerRect.bottom - 1;

    return withinX && downward && event.clientY <= maxY;
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const submenuHeights = Array.from(submenus)
      .filter((submenu) => submenu instanceof HTMLElement)
      .map((submenu) => submenu.offsetHeight);
    const maxHeight = submenuHeights.length > 0 ? Math.max(...submenuHeights) : 0;
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // `element` may be the list item itself (hover/focus routed via the `<li>`) or a
  // descendant such as the disclosure button (click routed via the button).
  const listItem = element.closest('.menu-list__list-item') ?? element;
  const menuitem = listItem.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
